import { useState } from "react";
import { Box, Button, TextField } from "@mui/material";
import BiometricIcon from "@mui/icons-material/Fingerprint";
import { useTranslation } from "react-i18next";
import { useSnackbar } from "notistack";
import { startRegistration } from "@simplewebauthn/browser";
import { useWebAuthnRegistrationMutation } from "../../../api/webauthn";
import { ERROR_SNACKBAR, SUCCESS_SNACKBAR } from "../../../consts";
import { detectDevice } from "../../../utils/deviceName";

interface RegisterPasskeyFormProps {
  onSuccess?: () => void;
}

const suggestDeviceName = (t: (key: string, options?: Record<string, unknown>) => string): string => {
  const { os, browser } = detectDevice();
  if (os && browser) return t("{{browser}} on {{os}}", { browser, os });
  return os ?? browser ?? "";
};

export const RegisterPasskeyForm = ({ onSuccess }: RegisterPasskeyFormProps) => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const { loading: registering, optionsMutation, verifyMutation } = useWebAuthnRegistrationMutation();

  const [deviceName, setDeviceName] = useState<string>(() => suggestDeviceName(t));

  const handleRegister = async () => {
    const { data: options, success: optionsSuccess } = await optionsMutation();
    if (!optionsSuccess || !options) {
      enqueueSnackbar(t("Failed to start biometric registration"), ERROR_SNACKBAR);
      return;
    }

    let credential;
    try {
      credential = await startRegistration({ optionsJSON: options });
    } catch (e) {
      enqueueSnackbar(t("Biometric registration was cancelled or is not available on this device."), ERROR_SNACKBAR);
      return;
    }

    const { success } = await verifyMutation(credential, deviceName);
    if (success) {
      setDeviceName("");
      window.dispatchEvent(new Event("credentialRegistered"));
      enqueueSnackbar(t("Passkey registered successfully"), SUCCESS_SNACKBAR);
      onSuccess?.();
    } else {
      enqueueSnackbar(t("Failed to register passkey"), ERROR_SNACKBAR);
    }
  };

  return (
    <Box
      sx={{
        display: "flex",
        gap: 1,
        // Stack on phones: the button label is long enough that a side-by-side
        // row squeezes the text field down to a few characters.
        flexDirection: { xs: "column", sm: "row" },
        alignItems: { xs: "stretch", sm: "flex-start" },
      }}
    >
      <TextField
        name="deviceName"
        label={t("Device name")}
        placeholder={t("e.g. Work laptop")}
        size="small"
        fullWidth
        value={deviceName}
        onChange={(e) => setDeviceName(e.target.value)}
      />
      <Button
        variant="contained"
        color="success"
        startIcon={<BiometricIcon />}
        onClick={handleRegister}
        loading={registering}
        disabled={!deviceName}
        sx={{
          whiteSpace: { xs: "normal", sm: "nowrap" },
          flexShrink: 0,
          width: { xs: "100%", sm: "auto" },
        }}
      >
        {t("Set up biometric login")}
      </Button>
    </Box>
  );
};
